import { WebShareApiSettings } from './types/web-share-api-settings';

const canUseWebShareApi = (shareSettings: WebShareApiSettings) =>
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  navigator.canShare?.(shareSettings);

const handleWebShare = (shareSettings: WebShareApiSettings) => {
  try {
    navigator.share(shareSettings);
  } catch (error) {
    console.error('Error sharing:', error);
  }
};

// Strip any [placeholder] content left in accidentally, along with the brackets
const removePlaceholders = (text: string) => text.replace(/\[[^\]]*\]/g, '');

const getWebShareSettings = (): WebShareApiSettings => {
  const textElement = document.querySelector('.wp-share-mobile-text');
  const titleElement = document.querySelector('.wp-share-mobile-title');
  const urlElement = document.querySelector('.wp-share-mobile-url');

  const currentPageUrl = new URL(window.location.href);
  // Replace page number after final slash with 1
  const actionUrlPage1 = currentPageUrl.href.replace(/\/([^/]*)$/, '/1');

  return {
    text: removePlaceholders(textElement?.textContent || '')
      .replace(/\n/g, '')
      .trim(),
    title: removePlaceholders(titleElement?.textContent || '')
      .replace(/\n/g, '')
      .trim(),
    url:
      removePlaceholders(urlElement?.textContent || '')
        .replace(/\n/g, '')
        .trim() || actionUrlPage1,
  };
};

// Button labels by language code; falls back to English
const shareButtonText: Record<string, { label: string; title: string }> = {
  de: { label: 'Teilen', title: 'Diese Seite teilen' },
  en: { label: 'Share', title: 'Share this page' },
  es: { label: 'Compartir', title: 'Compartir esta página' },
  nb: { label: 'Del', title: 'Del denne siden' },
  nn: { label: 'Del', title: 'Del denne sida' },
  no: { label: 'Del', title: 'Del denne siden' },
};

// Language code only, e.g. 'en' from window.pageJson.locale 'en-GB'
const getLanguage = () => (window.pageJson?.locale || 'en').split(/[-_]/)[0].toLowerCase();

// Get the button text for the page's language
const getShareButtonText = () => shareButtonText[getLanguage()] || shareButtonText.en;

// Network names for the built-in EN share buttons, keyed by the button's modifier class
const shareNetworkNames: Record<string, string> = {
  bluesky: 'BlueSky',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'X',
  whatsapp: 'WhatsApp',
};

// 'Share on {network}' by language code; falls back to English
const shareOnNetworkText: Record<string, string> = {
  de: 'Auf {network} teilen',
  en: 'Share on {network}',
  es: 'Compartir en {network}',
  nb: 'Del på {network}',
  nn: 'Del på {network}',
  no: 'Del på {network}',
};

// Set the translated text on each built-in EN share button as a 'data-wp-share-text' attribute,
// which the CSS shows as the button's text (the English text in the CSS is the fallback)
const translateShareButtons = () => {
  const template = shareOnNetworkText[getLanguage()] || shareOnNetworkText.en;

  document.querySelectorAll('.en__socialShare').forEach((button) => {
    const network = button.className.match(/en__socialShare--(\w+)/)?.[1];
    const networkName = network && shareNetworkNames[network];
    if (!networkName) return;

    button.setAttribute('data-wp-share-text', template.replace('{network}', networkName));
  });
};

const isDeviceThatUsesApps = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Set up the share buttons: translate the built-in EN buttons, and swap them for a single mobile
// share button on devices that can use the Web Share API
export const shareButtons = () => {
  const shareButtonsContainer = document.querySelector('.en__socialShares');
  if (!shareButtonsContainer) return;

  translateShareButtons();

  const webShareSettings = getWebShareSettings();

  const useMobileShareButton = isDeviceThatUsesApps && canUseWebShareApi(webShareSettings);

  if (!useMobileShareButton) {
    // Make sure built-in EN share buttons are visible if we're not using the mobile share button (e.g. on desktop)
    const enShareButtons = document.querySelectorAll('.en__socialShare');
    enShareButtons.forEach((button) =>
      (button as HTMLElement).style.setProperty('display', 'flex', 'important'),
    );

    return;
  }

  shareButtonsContainer.classList.add('wp-share-mobile-show');

  const { label, title } = getShareButtonText();

  shareButtonsContainer.insertAdjacentHTML(
    'beforeend',
    `<button aria-label='${title}' class='wp-share-mobile-button' title='${title}' type='button'>${label}</button>`,
  );

  const mobileShareButton = shareButtonsContainer.querySelector('.wp-share-mobile-button');
  mobileShareButton?.addEventListener('click', () => handleWebShare(webShareSettings));
};
